import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Edit, Archive, Link, Unlink, PiggyBank, Plus, Trash2, TrendingUp, Wallet, Calendar, BarChart3 } from 'lucide-react';
import {
  useDebt,
  useDebtSuggestions,
  useDeleteDebt,
  useUnlinkTransactionFromDebt,
  useUnlinkRecurrenceFromDebt,
  useLinkTransactionToDebt,
  useLinkRecurrenceToDebt,
  useBatchLinkTransactions,
  useDebtDrawdownState,
  useProvisionSchedule,
  useCreateDrawdown,
  useDeleteDrawdown,
} from '../hooks/useDebts.js';
import { useCreateProvision } from '../hooks/useProvisions.js';
import { useProvisionState } from '../hooks/useProvisionState.js';
import { useBankAccounts } from '../hooks/useBankAccounts.js';
import DebtProgressBar from '../components/DebtProgressBar.jsx';
import DebtScheduleTable from '../components/DebtScheduleTable.jsx';
import DebtTimelineChart from '../components/DebtTimelineChart.jsx';
import DebtProvisionChart from '../components/DebtProvisionChart.jsx';
import DebtForm from '../components/DebtForm.jsx';
import DebtLotAllocations from '../components/DebtLotAllocations.jsx';
import DebtProvisionTracker from '../components/DebtProvisionTracker.jsx';
import CategoryCombobox from '../components/CategoryCombobox.jsx';
import ProvisionDetailDrawer from '../components/forecasts/ProvisionDetailDrawer.jsx';
import LoadingSpinner from '../components/LoadingSpinner.jsx';
import Button from '../components/Button.jsx';
import { formatCurrency, formatDate } from '../lib/format.js';
import {
  SOURCE_TYPE_LABELS,
  SOURCE_TYPE_BADGE,
  REPAYMENT_MODE_LABELS,
  STATUS_LABELS,
  INTEREST_MODE_LABELS,
  computeEndDate,
} from '../lib/debtHelpers.js';

// ── Small Field ───────────────────────────────────────────────────────────────

function Field({ label, value }) {
  if (value == null || value === '') return null;
  return (
    <div className="flex justify-between items-start gap-3 py-1.5 text-sm border-b border-slate-50 last:border-0">
      <span className="text-slate-500 shrink-0">{label}</span>
      <span className="text-slate-800 text-right break-words">{value}</span>
    </div>
  );
}

// ── Linked tx row ─────────────────────────────────────────────────────────────

function LinkedTxRow({ tx, onUnlink, isPending }) {
  const isDebit = (tx.amount_cents ?? 0) < 0;
  const amount = Math.abs(tx.amount_cents ?? 0);
  const name = tx.clean_counterparty_name ?? tx.counterparty_name ?? tx.label ?? '—';
  const date = tx.settled_at ?? tx.operation_date;

  return (
    <div className="flex items-center justify-between gap-3 py-2 text-sm border-b border-slate-50 last:border-0">
      <div className="flex-1 min-w-0">
        <p className="font-medium text-slate-800 truncate">{name}</p>
        <p className="text-xs text-slate-500">{formatDate(date)}</p>
      </div>
      <span className={`tabular-nums font-medium shrink-0 ${isDebit ? 'text-slate-900' : 'text-green-600'}`}>
        {isDebit ? '-' : '+'}{formatCurrency(amount)}
      </span>
      <button
        type="button"
        onClick={onUnlink}
        disabled={isPending}
        className="shrink-0 text-xs text-slate-400 hover:text-red-600 transition-colors disabled:opacity-50 flex items-center gap-1"
        aria-label="Délier cette transaction"
      >
        <Unlink size={12} /> Délier
      </button>
    </div>
  );
}

// ── Linked recurrence row ─────────────────────────────────────────────────────

function LinkedRecRow({ rec, onUnlink, isPending }) {
  const label = rec.label_sample ?? rec.label_pattern ?? '—';
  return (
    <div className="flex items-center justify-between gap-3 py-2 text-sm border-b border-slate-50 last:border-0">
      <div className="flex-1 min-w-0">
        <p className="font-medium text-slate-800 truncate">{label}</p>
        <p className="text-xs text-slate-500">{rec.frequency}</p>
      </div>
      <span className="tabular-nums font-medium text-slate-900 shrink-0">
        {formatCurrency(Math.abs(rec.amount_cents ?? 0))}
      </span>
      <button
        type="button"
        onClick={onUnlink}
        disabled={isPending}
        className="shrink-0 text-xs text-slate-400 hover:text-red-600 transition-colors disabled:opacity-50 flex items-center gap-1"
        aria-label="Délier cette récurrence"
      >
        <Unlink size={12} /> Délier
      </button>
    </div>
  );
}

// ── Suggestions section ───────────────────────────────────────────────────────

function SuggestionsSection({ debtId }) {
  const { data: suggestions } = useDebtSuggestions(debtId);
  const batchLink = useBatchLinkTransactions();
  const linkRec = useLinkRecurrenceToDebt();

  const txList = suggestions?.transactions ?? [];
  const recList = suggestions?.recurrences ?? [];

  // Pré-cocher toutes les suggestions transactions par défaut
  const [checked, setChecked] = useState(() => new Set(txList.map((t) => t.id)));
  // Re-sync quand la liste change (après un link, les déjà-liées disparaissent)
  useEffect(() => {
    setChecked(new Set(txList.map((t) => t.id)));
  }, [txList.map((t) => t.id).join(',')]);

  if (!txList.length && !recList.length) return null;

  function toggle(id) {
    setChecked((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function toggleAll() {
    if (checked.size === txList.length) setChecked(new Set());
    else setChecked(new Set(txList.map((t) => t.id)));
  }

  const selectedCount = checked.size;

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-4 py-4 space-y-3">
      <h2 className="text-sm font-semibold text-slate-800">Suggestions de liens</h2>
      <p className="text-xs text-slate-500">
        Ces éléments ont un montant proche de la mensualité théorique (±&nbsp;5&nbsp;%).
      </p>

      {txList.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-medium text-slate-600">Transactions ({txList.length})</p>
            <button
              type="button"
              onClick={toggleAll}
              className="text-xs text-slate-500 hover:text-slate-800 underline"
            >
              {checked.size === txList.length ? 'Tout décocher' : 'Tout cocher'}
            </button>
          </div>
          <div className="divide-y divide-slate-50">
            {txList.map((tx) => {
              const name = tx.clean_counterparty_name ?? tx.counterparty_name ?? tx.label ?? '—';
              const isChecked = checked.has(tx.id);
              return (
                <label
                  key={tx.id}
                  className="flex items-center gap-3 py-2 text-sm cursor-pointer hover:bg-slate-50/50 px-2 -mx-2 rounded"
                >
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => toggle(tx.id)}
                    className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-800 truncate">{name}</p>
                    <p className="text-xs text-slate-500">
                      {formatDate(tx.settled_at ?? tx.operation_date)} · {formatCurrency(Math.abs(tx.amount_cents ?? 0))}
                    </p>
                  </div>
                </label>
              );
            })}
          </div>
          <div className="mt-3 flex items-center justify-end gap-2">
            <Button
              onClick={() => batchLink.mutate({ debtId, transactionIds: [...checked] })}
              disabled={selectedCount === 0 || batchLink.isPending}
              loading={batchLink.isPending}
              className="text-xs gap-1"
            >
              <Link size={12} /> Lier les {selectedCount} transactions sélectionnées
            </Button>
          </div>
          {batchLink.error && (
            <p className="text-xs text-red-600 mt-2">{batchLink.error.message}</p>
          )}
        </div>
      )}

      {recList.length > 0 && (
        <div className="pt-2 border-t border-slate-100">
          <p className="text-xs font-medium text-slate-600 mb-2">Récurrences ({recList.length})</p>
          <div className="divide-y divide-slate-50">
            {recList.slice(0, 5).map((rec) => {
              const label = rec.label_sample ?? rec.label_pattern ?? '—';
              return (
                <div key={rec.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-800 truncate">{label}</p>
                    <p className="text-xs text-slate-500">
                      {rec.frequency} · {formatCurrency(Math.abs(rec.amount_cents ?? 0))}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => linkRec.mutate({ debtId, recurrenceId: rec.id })}
                    disabled={linkRec.isPending}
                    className="shrink-0 text-xs text-sky-600 hover:text-sky-800 font-medium flex items-center gap-1 disabled:opacity-50"
                    aria-label={`Lier la récurrence ${label}`}
                  >
                    <Link size={12} /> Lier
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Bloc provision dédiée ─────────────────────────────────────────────────────

const inputClass =
  'w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-slate-500 focus:border-slate-500 disabled:opacity-50';

/**
 * Modale légère pour activer une provision depuis la page détail.
 * Utilisée quand aucune provision n'est encore liée à la dette.
 */
function ActivateProvisionModal({ open, onClose, debt }) {
  const createProvision = useCreateProvision();
  const { data: bankAccountsData } = useBankAccounts();
  const bankAccounts = Array.isArray(bankAccountsData)
    ? bankAccountsData
    : (bankAccountsData?.accounts ?? bankAccountsData?.data ?? []);

  const defaultFunding = debt.capital_cents && debt.duration_months
    ? String(Math.round(debt.capital_cents / debt.duration_months) / 100)
    : '';

  const defaultTargetDate = (() => {
    if (!debt.start_date || !debt.duration_months) return '';
    const d = new Date(debt.start_date);
    d.setMonth(d.getMonth() + debt.duration_months);
    return d.toISOString().slice(0, 10);
  })();

  const [form, setForm] = useState({
    name: `Provision ${debt.name ?? ''}`,
    category_id: '',
    funding_monthly: defaultFunding,
    target_amount: debt.capital_cents ? String(debt.capital_cents / 100) : '',
    target_date: defaultTargetDate,
    bank_account_id: '',
    notes: '',
    start_date: debt.start_date ?? '',
  });
  const [errors, setErrors] = useState({});

  // ESC + lock scroll
  useEffect(() => {
    function handler(e) { if (e.key === 'Escape') onClose?.(); }
    if (open) document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  function set(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const errs = {};
    if (!form.name.trim()) errs.name = 'Nom requis';
    if (!form.category_id) errs.category_id = 'Catégorie requise';
    const funding = parseFloat(form.funding_monthly);
    if (!form.funding_monthly || isNaN(funding) || funding <= 0) {
      errs.funding = 'Dotation mensuelle requise (> 0)';
    }
    if (Object.keys(errs).length) { setErrors(errs); return; }

    const payload = {
      name:                  form.name.trim(),
      category_id:           form.category_id,
      funding_cents_monthly: Math.round(funding * 100),
      start_date:            form.start_date || new Date().toISOString().slice(0, 10),
      debt_id:               debt.id,
    };

    const targetAmount = parseFloat(form.target_amount);
    if (!isNaN(targetAmount) && targetAmount > 0) {
      payload.target_amount_cents = Math.round(targetAmount * 100);
    }
    if (form.target_date) payload.target_date = form.target_date;
    if (form.bank_account_id) payload.bank_account_id = form.bank_account_id;
    if (form.notes?.trim()) payload.notes = form.notes.trim();

    await createProvision.mutateAsync(payload);
    onClose?.();
  }

  function FieldM({ label, children, error, required }) {
    return (
      <div className="space-y-1">
        <label className="block text-xs font-medium text-slate-700">
          {label}{required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
        {children}
        {error && <p className="text-xs text-red-600">{error}</p>}
      </div>
    );
  }

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/40" aria-hidden onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Activer une provision"
        className="fixed inset-x-4 top-1/2 -translate-y-1/2 z-50 bg-white rounded-2xl shadow-2xl max-h-[85dvh] overflow-y-auto md:inset-x-auto md:left-1/2 md:-translate-x-1/2 md:w-[480px]"
      >
        <div className="sticky top-0 bg-white flex items-center justify-between border-b border-slate-100 px-4 py-3 z-10">
          <div className="flex items-center gap-2">
            <PiggyBank size={16} className="text-emerald-600" aria-hidden />
            <h2 className="text-base font-semibold text-slate-900">Activer une provision</h2>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:outline-none"
            aria-label="Fermer"
          >
            <span aria-hidden>×</span>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-4 py-5 space-y-4">
          <p className="text-xs text-slate-500">
            Les valeurs sont pré-calculées depuis le capital et la durée du prêt.
          </p>

          <FieldM label="Nom de la provision" error={errors.name} required>
            <input
              type="text"
              className={inputClass}
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              disabled={createProvision.isPending}
            />
          </FieldM>

          <FieldM label="Catégorie" error={errors.category_id} required>
            <CategoryCombobox
              value={form.category_id}
              onChange={(v) => set('category_id', v ?? '')}
              placeholder="Catégorie de provision…"
            />
          </FieldM>

          <div className="grid grid-cols-2 gap-3">
            <FieldM label="Dotation mensuelle (€)" error={errors.funding} required>
              <input
                type="number"
                className={inputClass}
                value={form.funding_monthly}
                onChange={(e) => set('funding_monthly', e.target.value)}
                min="0.01"
                step="0.01"
                disabled={createProvision.isPending}
              />
            </FieldM>

            <FieldM label="Montant cible (€)">
              <input
                type="number"
                className={inputClass}
                value={form.target_amount}
                onChange={(e) => set('target_amount', e.target.value)}
                min="0.01"
                step="0.01"
                disabled={createProvision.isPending}
              />
            </FieldM>
          </div>

          <FieldM label="Date cible">
            <input
              type="date"
              className={inputClass}
              value={form.target_date}
              onChange={(e) => set('target_date', e.target.value)}
              disabled={createProvision.isPending}
            />
          </FieldM>

          <FieldM label="Compte bancaire associé">
            <select
              className={inputClass}
              value={form.bank_account_id}
              onChange={(e) => set('bank_account_id', e.target.value)}
              disabled={createProvision.isPending}
            >
              <option value="">Aucun pour l'instant</option>
              {bankAccounts.map((acc) => (
                <option key={acc.id} value={acc.id}>
                  {acc.name ?? acc.label ?? acc.id}
                </option>
              ))}
            </select>
          </FieldM>

          <FieldM label="Notes">
            <textarea
              className={`${inputClass} resize-none`}
              value={form.notes}
              onChange={(e) => set('notes', e.target.value)}
              rows={2}
              placeholder="Optionnel"
              disabled={createProvision.isPending}
            />
          </FieldM>

          {createProvision.error && (
            <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">
              {createProvision.error.message}
            </p>
          )}

          <div className="flex gap-2 pt-2">
            <Button
              type="submit"
              loading={createProvision.isPending}
              disabled={createProvision.isPending}
              className="flex-1"
            >
              Activer la provision
            </Button>
            <Button type="button" variant="ghost" onClick={onClose} disabled={createProvision.isPending}>
              Annuler
            </Button>
          </div>
        </form>
      </div>
    </>
  );
}

/**
 * Bloc affiché sur la page détail d'une dette quand une provision est liée.
 */
function LinkedProvisionBlock({ provision, onOpenDrawer }) {
  const { data: state } = useProvisionState(provision.id);

  const balance = state?.balance_cents ?? provision.balance_cents ?? 0;
  const target  = provision.target_amount_cents ?? 0;
  const pct     = target > 0 ? Math.min(100, Math.round((balance / target) * 100)) : 0;

  const healthColor =
    pct >= 90 ? 'bg-emerald-500'
    : pct >= 50 ? 'bg-amber-400'
    : 'bg-red-400';

  return (
    <div className="bg-white rounded-xl border border-emerald-200 shadow-sm px-4 py-4 space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <PiggyBank size={16} className="text-emerald-600 shrink-0" aria-hidden />
          <h2 className="text-sm font-semibold text-slate-800">Provision dédiée</h2>
        </div>
        <button
          type="button"
          onClick={onOpenDrawer}
          className="text-xs text-emerald-700 hover:text-emerald-900 underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 rounded"
        >
          Voir / gérer
        </button>
      </div>

      <p className="text-sm font-medium text-slate-700">{provision.name}</p>

      {/* Barre de progression */}
      {target > 0 && (
        <div>
          <div className="flex justify-between text-[11px] text-slate-500 mb-1">
            <span className="tabular-nums">{formatCurrency(balance)}</span>
            <span className="tabular-nums">{pct} % · objectif {formatCurrency(target)}</span>
          </div>
          <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${healthColor}`}
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
      )}

      {/* Métriques */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <p className="text-xs text-slate-400">Dotation mensuelle</p>
          <p className="text-sm font-semibold tabular-nums text-slate-800">
            {formatCurrency(provision.funding_cents_monthly)}
          </p>
        </div>
        {provision.target_date && (
          <div>
            <p className="text-xs text-slate-400">Date cible</p>
            <p className="text-sm font-semibold text-slate-800">
              {formatDate(provision.target_date)}
            </p>
          </div>
        )}
      </div>

      {/* Compte bancaire */}
      <p className="text-xs text-slate-500">
        {provision.bank_account_id
          ? `Compte associé : ${provision.bank_account_name ?? provision.bank_account_id}`
          : 'Pas encore connecté à un compte bancaire'}
      </p>

      <p className="text-[11px] text-slate-400">
        Statut : {provision.status === 'active' ? 'Active' : provision.status}
      </p>
    </div>
  );
}

// ── Revolving section ─────────────────────────────────────────────────────────

/**
 * Formulaire d'ajout d'un tirage.
 */
function AddDrawdownForm({ debtId, onSuccess }) {
  const createDrawdown = useCreateDrawdown();
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ amount: '', drawn_date: today, label: '' });
  const [errors, setErrors] = useState({});

  function set(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const errs = {};
    const amount = parseFloat(form.amount);
    if (!form.amount || isNaN(amount) || amount <= 0) errs.amount = 'Montant requis (> 0)';
    if (!form.drawn_date) errs.drawn_date = 'Date requise';
    if (Object.keys(errs).length) { setErrors(errs); return; }

    try {
      await createDrawdown.mutateAsync({
        debtId,
        amount_cents: Math.round(amount * 100),
        drawn_date: form.drawn_date,
        label: form.label.trim() || undefined,
      });
      setForm({ amount: '', drawn_date: today, label: '' });
      onSuccess?.();
    } catch (err) {
      setErrors({
        global: err?.message?.includes('planned_repayment_date')
          ? "Cette enveloppe n'a pas de date in fine configurée. Modifie la dette pour en ajouter une."
          : (err?.message ?? "Erreur lors de l'enregistrement du tirage"),
      });
    }
  }

  const addInputClass =
    'w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500 disabled:opacity-50';

  return (
    <form onSubmit={handleSubmit} className="space-y-3 pt-3 border-t border-slate-100">
      <p className="text-xs font-medium text-slate-600">Ajouter un tirage</p>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <label className="block text-xs font-medium text-slate-700">
            Montant (€) <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            className={addInputClass}
            value={form.amount}
            onChange={(e) => set('amount', e.target.value)}
            min="0.01"
            step="0.01"
            placeholder="5000"
            disabled={createDrawdown.isPending}
          />
          {errors.amount && <p className="text-xs text-red-600">{errors.amount}</p>}
        </div>

        <div className="space-y-1">
          <label className="block text-xs font-medium text-slate-700">
            Date du tirage <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            className={addInputClass}
            value={form.drawn_date}
            onChange={(e) => set('drawn_date', e.target.value)}
            disabled={createDrawdown.isPending}
          />
          {errors.drawn_date && <p className="text-xs text-red-600">{errors.drawn_date}</p>}
        </div>
      </div>

      <div className="space-y-1">
        <label className="block text-xs font-medium text-slate-700">Label (optionnel)</label>
        <input
          type="text"
          className={addInputClass}
          value={form.label}
          onChange={(e) => set('label', e.target.value)}
          placeholder="Virement associé, objet…"
          disabled={createDrawdown.isPending}
        />
      </div>

      {errors.global && (
        <p className="text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2">{errors.global}</p>
      )}

      <Button
        type="submit"
        loading={createDrawdown.isPending}
        disabled={createDrawdown.isPending}
        className="text-xs"
      >
        <Plus size={13} /> Enregistrer le tirage
      </Button>
    </form>
  );
}

/**
 * Section complète de suivi des tirages pour une dette revolving.
 */
function RevolvingSection({ debt }) {
  const { data: state, isLoading: stateLoading } = useDebtDrawdownState(debt.id);
  const deleteDrawdown = useDeleteDrawdown();

  // Provision schedule : 24 mois autour d'aujourd'hui
  const today = new Date().toISOString().slice(0, 10);
  const fromDate = today.slice(0, 7) + '-01';
  const toDate = (() => {
    const d = new Date(today);
    d.setMonth(d.getMonth() + 24);
    return d.toISOString().slice(0, 7) + '-01';
  })();
  const { data: schedule = [] } = useProvisionSchedule(debt.id, { from: fromDate, to: toDate });

  if (stateLoading) {
    return (
      <div className="bg-white rounded-xl border border-purple-200 shadow-sm px-4 py-6 flex justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  const s = state ?? {};
  const drawdowns = s.drawdowns ?? [];
  const drawnTotal = s.drawn_total_cents ?? 0;
  const available = s.available_cents ?? 0;
  const capital = s.capital_cents ?? debt.capital_cents ?? 0;
  const monthlyProv = s.monthly_provision_cents ?? 0;
  const provisionedToDate = s.provisioned_to_date_cents ?? 0;
  const accruedNow = s.accrued_interest_now_cents ?? 0;
  const accruedAtRepayment = s.accrued_interest_at_repayment_cents ?? 0;
  const totalDue = s.total_due_in_fine_cents ?? 0;
  const coveragePct = s.coverage_pct ?? 0;
  const provisionAccount = s.provision_account ?? null;
  const plannedRepayment = s.planned_repayment_date ?? debt.planned_repayment_date ?? null;

  const coverageClass = coveragePct >= 90
    ? 'text-green-700 bg-green-50'
    : coveragePct >= 50
    ? 'text-orange-700 bg-orange-50'
    : 'text-red-700 bg-red-50';

  return (
    <div className="space-y-4">
      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Tiré / Disponible */}
        <div className="bg-white rounded-xl border border-purple-200 shadow-sm px-4 py-4">
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">
            Tiré / Plafond
          </p>
          <p className="text-2xl font-bold tabular-nums text-purple-800">
            {formatCurrency(drawnTotal)}
          </p>
          <p className="text-xs text-slate-500 mt-1">
            sur {formatCurrency(capital)} · disponible : <span className="font-medium text-slate-700">{formatCurrency(available)}</span>
          </p>
          {capital > 0 && (
            <div className="mt-2 h-1.5 bg-purple-100 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full bg-purple-500 transition-all"
                style={{ width: `${Math.min(100, Math.round((drawnTotal / capital) * 100))}%` }}
              />
            </div>
          )}
        </div>

        {/* Provision mensuelle */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-4 py-4">
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1 flex items-center gap-1.5">
            <BarChart3 size={13} aria-hidden /> Provision mensuelle
          </p>
          <p className="text-2xl font-bold tabular-nums text-slate-900">
            {formatCurrency(monthlyProv)}
          </p>
          <p className="text-xs text-slate-500 mt-1">cumulée sur l'ensemble des tirages</p>
        </div>

        {/* Provisionné à date vs compte dédié */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-4 py-4">
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1 flex items-center gap-1.5">
            <Wallet size={13} aria-hidden /> Provisionné à date
          </p>
          <p className="text-2xl font-bold tabular-nums text-slate-900">
            {formatCurrency(provisionedToDate)}
          </p>
          {provisionAccount ? (
            <p className="text-xs text-slate-500 mt-1">
              Solde compte dédié :{' '}
              <span className={`font-medium ${
                provisionAccount.balance_cents >= provisionedToDate
                  ? 'text-green-600' : 'text-red-600'
              }`}>
                {formatCurrency(provisionAccount.balance_cents)}
              </span>
              {' '}({provisionAccount.name})
            </p>
          ) : (
            <p className="text-xs text-slate-400 mt-1">Pas de compte dédié configuré</p>
          )}
        </div>

        {/* Intérêts capitalisés */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-4 py-4">
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1 flex items-center gap-1.5">
            <TrendingUp size={13} aria-hidden /> Intérêts capitalisés
          </p>
          <p className="text-2xl font-bold tabular-nums text-amber-700">
            {formatCurrency(accruedNow)}
          </p>
          <p className="text-xs text-slate-500 mt-1">
            Projetés in fine : <span className="font-medium text-amber-800">{formatCurrency(accruedAtRepayment)}</span>
          </p>
        </div>

        {/* Total dû in fine */}
        <div className="bg-white rounded-xl border border-red-200 shadow-sm px-4 py-4">
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">
            Total dû in fine
          </p>
          <p className="text-2xl font-bold tabular-nums text-red-800">
            {formatCurrency(totalDue)}
          </p>
          <p className="text-xs text-slate-500 mt-1">
            Capital tiré + intérêts capitalisés
          </p>
        </div>

        {/* Date in fine + couverture */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-4 py-4">
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1 flex items-center gap-1.5">
            <Calendar size={13} aria-hidden /> Date in fine
          </p>
          <p className="text-xl font-bold text-slate-900">
            {plannedRepayment ? formatDate(plannedRepayment) : '—'}
          </p>
          <p className={`text-xs font-medium mt-2 rounded-full inline-block px-2 py-0.5 ${coverageClass}`}>
            Couverture : {coveragePct} %
          </p>
        </div>
      </div>

      {/* Liste des tirages + formulaire ajout */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-4 py-4 space-y-3">
        <h2 className="text-sm font-semibold text-slate-800">
          Tirages enregistrés
          <span className="ml-2 text-xs font-normal text-slate-500">({drawdowns.length})</span>
        </h2>

        {drawdowns.length === 0 ? (
          <p className="text-sm text-slate-400 italic">Aucun tirage enregistré pour l'instant.</p>
        ) : (
          <div className="overflow-x-auto -mx-1">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-slate-500 border-b border-slate-100">
                  <th className="pb-2 pr-3 font-medium sticky left-0 bg-white">Date</th>
                  <th className="pb-2 pr-3 font-medium">Montant</th>
                  <th className="pb-2 pr-3 font-medium hidden md:table-cell">Label</th>
                  <th className="pb-2 pr-3 font-medium">Prov./mois</th>
                  <th className="pb-2 pr-3 font-medium hidden md:table-cell">Périodes</th>
                  <th className="pb-2 font-medium"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {drawdowns.map((d) => (
                  <tr key={d.id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="py-2 pr-3 tabular-nums text-slate-700 sticky left-0 bg-white hover:bg-slate-50/50">
                      {formatDate(d.drawn_date)}
                    </td>
                    <td className="py-2 pr-3 tabular-nums font-semibold text-slate-900">
                      {formatCurrency(d.amount_cents)}
                    </td>
                    <td className="py-2 pr-3 text-slate-500 hidden md:table-cell max-w-[140px] truncate">
                      {d.label ?? '—'}
                    </td>
                    <td className="py-2 pr-3 tabular-nums text-purple-700 font-medium">
                      {formatCurrency(d.monthly_provision_cents)}
                    </td>
                    <td className="py-2 pr-3 text-slate-500 hidden md:table-cell">
                      {d.periods_remaining} mois
                    </td>
                    <td className="py-2">
                      <button
                        type="button"
                        onClick={() => deleteDrawdown.mutate({ debtId: debt.id, drawdownId: d.id })}
                        disabled={deleteDrawdown.isPending}
                        className="text-slate-400 hover:text-red-600 transition-colors disabled:opacity-50"
                        aria-label={`Supprimer le tirage du ${formatDate(d.drawn_date)}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <AddDrawdownForm debtId={debt.id} />
      </div>

      {/* Graphe provision cumulée */}
      {schedule.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-4 py-4">
          <h2 className="text-sm font-semibold text-slate-800 mb-3">
            Accumulation de la provision
          </h2>
          <DebtProvisionChart
            schedule={schedule}
            drawnTotalCents={drawnTotal}
            plannedRepayment={plannedRepayment}
          />
        </div>
      )}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function DebtDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [editOpen, setEditOpen] = useState(false);
  const [activateProvisionOpen, setActivateProvisionOpen] = useState(false);
  const [provisionDrawerOpen, setProvisionDrawerOpen] = useState(false);

  const { data, isLoading, error } = useDebt(id);
  const deleteDebt = useDeleteDebt();
  const unlinkTx = useUnlinkTransactionFromDebt();
  const unlinkRec = useUnlinkRecurrenceFromDebt();

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
        Impossible de charger la dette : {error.message}
      </div>
    );
  }

  if (!data) return null;

  // Le backend renvoie directement la dette au top-level + schedule +
  // linked_transactions + linked_recurrences. On mappe vers des noms courts.
  const debt = data;
  const schedule = data.schedule ?? [];
  const transactions = data.linked_transactions ?? [];
  const recurrences = data.linked_recurrences ?? [];
  const endDate = computeEndDate(debt);
  const sourceBadge = SOURCE_TYPE_BADGE[debt.source_type] ?? SOURCE_TYPE_BADGE.other;
  const sourceLabel = SOURCE_TYPE_LABELS[debt.source_type] ?? debt.source_type;
  const statusCfg = STATUS_LABELS[debt.status] ?? STATUS_LABELS.active;
  const modeLabel = REPAYMENT_MODE_LABELS[debt.repayment_mode] ?? debt.repayment_mode;

  // Provision liée — champ retourné par GET /v1/debts/:id dans progress.linked_provision
  const linkedProvision = debt.progress?.linked_provision ?? null;

  async function handleArchive() {
    if (!confirm(`Archiver la dette « ${debt.name} » ?`)) return;
    await deleteDebt.mutateAsync(debt.id);
    navigate('/dettes');
  }

  // Item minimal pour ProvisionDetailDrawer (qui attend un "forecast item")
  const provisionItem = linkedProvision
    ? {
        id:                    linkedProvision.id,
        name:                  linkedProvision.name,
        funding_cents_monthly: linkedProvision.funding_cents_monthly,
        cap_soft_cents:        linkedProvision.target_amount_cents ?? null,
        flow_type:             'expense',
        status:                linkedProvision.status,
      }
    : null;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start gap-3">
        <button
          type="button"
          onClick={() => navigate('/dettes')}
          className="mt-0.5 p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors shrink-0"
          aria-label="Retour à la liste des dettes"
        >
          <ArrowLeft size={18} />
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-lg font-semibold text-slate-900">{debt.name}</h1>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${sourceBadge}`}>
              {sourceLabel}
            </span>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusCfg.badge}`}>
              {statusCfg.label}
            </span>
          </div>
        </div>

        <div className="flex gap-2 shrink-0">
          <Button
            variant="ghost"
            className="text-xs gap-1"
            onClick={() => setEditOpen(true)}
          >
            <Edit size={13} /> Éditer
          </Button>
          <Button
            variant="danger"
            className="text-xs gap-1"
            onClick={handleArchive}
            loading={deleteDebt.isPending}
          >
            <Archive size={13} /> Archiver
          </Button>
        </div>
      </div>

      {/* Summary grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-4 py-4">
          <h2 className="text-xs font-semibold uppercase text-slate-500 mb-3 tracking-wide">Résumé</h2>
          <Field label="Capital emprunté" value={formatCurrency(debt.capital_cents)} />
          <div className="flex justify-between items-start gap-3 py-1.5 text-sm border-b border-slate-50">
            <span className="text-slate-500 shrink-0">Taux annuel</span>
            <span className="text-slate-800 text-right break-words">
              {debt.annual_rate_pct} %
              {debt.monthly_payment_cents != null && (
                <span className="text-xs text-gray-500 italic ml-1">
                  (calculé d'après la mensualité saisie)
                </span>
              )}
            </span>
          </div>
          {debt.monthly_payment_cents != null && (
            <Field
              label="Mensualité saisie"
              value={formatCurrency(debt.monthly_payment_cents)}
            />
          )}
          <Field label="Durée" value={`${debt.duration_months} mois`} />
          <Field label="Mode" value={modeLabel} />
          {debt.repayment_mode === 'sales_proportional' && (
            <>
              <Field label="% CA" value={`${debt.sales_pct} %`} />
              <Field label="CA mensuel estimé" value={formatCurrency(debt.monthly_ca_estimate_cents)} />
            </>
          )}
          <Field label="Début" value={formatDate(debt.start_date)} />
          <Field label="Fin projetée" value={formatDate(endDate)} />
          {debt.grace_period_months > 0 && (
            <Field label="Différé" value={`${debt.grace_period_months} mois`} />
          )}
          {debt.notes && (
            <div className="py-1.5 text-sm">
              <span className="text-slate-500">Notes</span>
              <p className="mt-1 text-slate-700 italic bg-slate-50 rounded px-3 py-2 text-xs">
                {debt.notes}
              </p>
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-4 py-4">
          <h2 className="text-xs font-semibold uppercase text-slate-500 mb-3 tracking-wide">Progression</h2>
          <DebtProgressBar
            paid_cents={debt.paid_cents ?? 0}
            total_cents={debt.capital_cents ?? 0}
            size="lg"
            isOverdue={debt.is_overdue}
          />
          <div className="mt-4 grid grid-cols-2 gap-3">
            <div>
              <p className="text-xs text-slate-500">Mensualité</p>
              <p className="text-base font-semibold tabular-nums text-slate-900">
                {formatCurrency(debt.monthly_payment_cents)}
              </p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Transactions liées</p>
              <p className="text-base font-semibold tabular-nums text-slate-900">
                {debt.tx_count ?? 0}
              </p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Restant dû</p>
              <p className="text-base font-semibold tabular-nums text-amber-700">
                {formatCurrency(debt.remaining_cents)}
              </p>
            </div>
            {debt.next_payment_date && (
              <div>
                <p className="text-xs text-slate-500">Prochaine échéance</p>
                <p className="text-sm font-medium text-slate-700">
                  {formatDate(debt.next_payment_date)}
                </p>
                <p className="text-xs text-slate-500 tabular-nums">
                  {formatCurrency(debt.next_payment_amount_cents)}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Lots financés par cette dette */}
      <DebtLotAllocations debt={debt} />

      {/* Bloc provision dédiée.
          provision_scope actif → le suivi adossé au compte bancaire réel
          remplace l'ancien module `provisions` (virtuel, catégoriel) : afficher
          les deux donnerait deux « montant provisionné » contradictoires. */}
      {debt.provision_scope && debt.provision_scope !== 'none' ? (
        <DebtProvisionTracker debt={debt} />
      ) : linkedProvision ? (
        <LinkedProvisionBlock
          provision={linkedProvision}
          onOpenDrawer={() => setProvisionDrawerOpen(true)}
        />
      ) : (
        <div className="bg-slate-50 rounded-xl border border-slate-200 px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-slate-500">
            <PiggyBank size={15} aria-hidden />
            <p className="text-sm">Aucune provision liée à ce prêt</p>
          </div>
          <button
            type="button"
            onClick={() => setActivateProvisionOpen(true)}
            className="shrink-0 flex items-center gap-1.5 text-xs text-emerald-700 hover:text-emerald-900 font-medium underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 rounded"
          >
            <Plus size={13} aria-hidden /> Activer une provision
          </button>
        </div>
      )}

      {/* Section revolving (tirages progressifs) OU timeline classique */}
      {debt.is_revolving ? (
        <RevolvingSection debt={debt} />
      ) : (
        <>
          {/* Timeline chart */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-4 py-4">
            <h2 className="text-sm font-semibold text-slate-800 mb-3">Évolution du capital</h2>
            <DebtTimelineChart
              schedule={schedule}
              transactions={transactions}
              capital={debt.capital_cents ?? 0}
            />
          </div>

          {/* Tableau d'amortissement — théorique avec indicateur visuel (vert pâle)
              sur les lignes déjà couvertes par une transaction liée */}
          {schedule.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-4 py-4">
              <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
                <h2 className="text-sm font-semibold text-slate-800">
                  Tableau d'amortissement
                  <span className="ml-2 text-xs font-normal text-slate-500">
                    {transactions.length} / {schedule.length} mensualités payées
                  </span>
                </h2>
                {transactions.length > 0 && (
                  <details className="text-xs text-slate-500">
                    <summary className="cursor-pointer hover:text-slate-700">Voir les tx liées</summary>
                    <div className="divide-y divide-slate-50 mt-2 max-w-md">
                      {[...transactions]
                        .sort((a, b) => (b.operation_date ?? '').localeCompare(a.operation_date ?? ''))
                        .map((tx) => (
                          <LinkedTxRow
                            key={tx.id}
                            tx={tx}
                            onUnlink={() => unlinkTx.mutate({ debtId: id, transactionId: tx.id })}
                            isPending={unlinkTx.isPending}
                          />
                        ))}
                    </div>
                  </details>
                )}
              </div>
              <DebtScheduleTable
                schedule={schedule}
                linkedTransactions={transactions}
              />
            </div>
          )}
        </>
      )}

      {/* Linked recurrences */}
      {recurrences.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-4 py-4">
          <h2 className="text-sm font-semibold text-slate-800 mb-3">
            Récurrences liées
            <span className="ml-2 text-xs font-normal text-slate-500">({recurrences.length})</span>
          </h2>
          <div className="divide-y divide-slate-50">
            {recurrences.map((rec) => (
              <LinkedRecRow
                key={rec.id}
                rec={rec}
                onUnlink={() => unlinkRec.mutate({ debtId: id, recurrenceId: rec.id })}
                isPending={unlinkRec.isPending}
              />
            ))}
          </div>
        </div>
      )}

      <SuggestionsSection debtId={id} />

      {/* Modaux / Drawers */}
      <DebtForm open={editOpen} onClose={() => setEditOpen(false)} debt={debt} />

      <ActivateProvisionModal
        open={activateProvisionOpen}
        onClose={() => setActivateProvisionOpen(false)}
        debt={debt}
      />

      {provisionDrawerOpen && linkedProvision && (
        <ProvisionDetailDrawer
          isOpen
          onClose={() => setProvisionDrawerOpen(false)}
          provisionId={linkedProvision.id}
          provisionItem={provisionItem}
        />
      )}
    </div>
  );
}
